import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { companyInfo } from '../data/mock';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be connected to backend later
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Let's discuss how Blue Bird can transform your supply chain operations
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12 max-w-7xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-2">
              <div className="sticky top-32">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Contact Information</h2>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Have questions? We're here to help. Reach out to us through any of the following channels.
                </p>

                <div className="space-y-6">
                  <Card className="border-2 border-slate-200 hover:border-orange-300 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="text-orange-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">Address</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">
                            {companyInfo.address}<br />
                            Pincode: {companyInfo.pincode}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-200 hover:border-cyan-300 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="text-cyan-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">Phone</h3>
                          <a 
                            href={`tel:${companyInfo.phone}`}
                            className="text-sm text-slate-600 hover:text-orange-500 transition-colors"
                          >
                            {companyInfo.phone}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-200 hover:border-green-300 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="text-green-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">Email</h3>
                          <a 
                            href={`mailto:${companyInfo.email}`}
                            className="text-sm text-slate-600 hover:text-orange-500 transition-colors"
                          >
                            {companyInfo.email}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="mt-8 p-6 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl text-white">
                  <h3 className="font-bold text-lg mb-2">Business Hours</h3>
                  <p className="text-sm text-orange-50">Monday - Saturday: 9:00 AM - 6:00 PM</p>
                  <p className="text-sm text-orange-50">Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-2 border-slate-200 shadow-xl">
                <CardContent className="p-8 md:p-10">
                  <h2 className="text-3xl font-bold text-slate-900 mb-6">Send us a Message</h2>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="text-green-600" size={48} />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h3>
                      <p className="text-slate-600">
                        Your message has been received. We'll get back to you within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Full Name *
                          </label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="border-2 border-slate-200 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@company.com"
                            required
                            className="border-2 border-slate-200 focus:border-orange-500"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Phone Number *
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 9876543210"
                            required
                            className="border-2 border-slate-200 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Company Name
                          </label>
                          <Input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your Company"
                            className="border-2 border-slate-200 focus:border-orange-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Service Interested In
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full border-2 border-slate-200 rounded-md px-3 py-2 focus:border-orange-500 focus:outline-none"
                        >
                          <option value="">Select a service</option>
                          <option value="warehousing">Warehousing Solutions</option>
                          <option value="rail">Rail Logistics</option>
                          <option value="road">Road Transportation</option>
                          <option value="air">Air Freight</option>
                          <option value="consulting">SCM Consultancy</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Message *
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your logistics needs..."
                          required
                          rows={6}
                          className="border-2 border-slate-200 focus:border-orange-500"
                        />
                      </div>

                      <Button 
                        type="submit"
                        size="lg"
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white text-base py-6"
                      >
                        Send Message
                        <Send className="ml-2" size={20} />
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">Visit Our Office</h2>
            <div className="bg-slate-200 rounded-2xl overflow-hidden" style={{ height: '400px' }}>
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-300 to-slate-400">
                <div className="text-center">
                  <MapPin className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-700 font-medium">Map Integration Available</p>
                  <p className="text-sm text-slate-600 mt-2">
                    Room 265, Chenoy Trade Center, Secunderabad - 500003
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
